import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'

import enCommon from './locales/en/common.json'
import enAuth from './locales/en/auth.json'
import enOnboarding from './locales/en/onboarding.json'
import enApp from './locales/en/app.json'
import esCommon from './locales/es/common.json'
import esAuth from './locales/es/auth.json'
import esOnboarding from './locales/es/onboarding.json'
import esApp from './locales/es/app.json'
import frCommon from './locales/fr/common.json'
import frAuth from './locales/fr/auth.json'
import frOnboarding from './locales/fr/onboarding.json'
import frApp from './locales/fr/app.json'
import ptCommon from './locales/pt/common.json'
import ptAuth from './locales/pt/auth.json'
import ptOnboarding from './locales/pt/onboarding.json'
import ptApp from './locales/pt/app.json'

/** Persisted UI chrome language (auth + onboarding). Independent of study content language. */
export const UI_LANGUAGE_STORAGE_KEY = 'fairgen.uiLanguage'

/**
 * First-class UI chrome languages. Portuguese is Brazilian (`pt-BR`).
 * Files still live under `locales/pt/` (the BR pack). A `pt-PT` pack can
 * land later without renaming content-language `pt` on the wire.
 */
export const UI_LANGUAGES = ['en', 'es', 'fr', 'pt-BR'] as const
export type UiLanguage = (typeof UI_LANGUAGES)[number]

const PT_BR_RESOURCES = {
  common: ptCommon,
  auth: ptAuth,
  onboarding: ptOnboarding,
  app: ptApp,
}

function isUiLanguage(value: string): value is UiLanguage {
  return (UI_LANGUAGES as ReadonlyArray<string>).includes(value)
}

/**
 * Map an i18n / navigator / BCP-47 tag onto a supported UI language.
 * `pt`, `pt-BR`, and `pt-PT` currently resolve to `pt-BR` so a Portugal
 * locale can be added later without a second storage migration.
 */
export function resolveUiLanguage(lng: string | undefined | null): UiLanguage {
  const raw = (lng || 'en').trim()
  if (!raw) {
    return 'en'
  }
  if (isUiLanguage(raw)) {
    return raw
  }
  const lower = raw.toLowerCase()
  if (lower === 'pt' || lower.startsWith('pt-')) {
    return 'pt-BR'
  }
  const primary = lower.split('-')[0]
  if (primary === 'en' || primary === 'es' || primary === 'fr') {
    return primary
  }
  return 'en'
}

/**
 * Intl locale for numbers and dates. Spanish UI stays Latin-American (es-MX);
 * Portuguese chrome is Brazilian (`pt-BR`).
 */
export function intlLocaleForUi(lng: string | undefined | null): string {
  switch (resolveUiLanguage(lng)) {
    case 'es':
      return 'es-MX'
    case 'fr':
      return 'fr-FR'
    case 'pt-BR':
      return 'pt-BR'
    default:
      return 'en-US'
  }
}

/**
 * Cache scope: one-shot per page load for init; language changes are user-driven.
 * Detection uses localStorage first, then navigator language (es-* → es,
 * pt / pt-* → pt-BR). Stored `pt` migrates to `pt-BR`.
 */
export function detectUiLanguage(): UiLanguage {
  if (typeof window === 'undefined') {
    return 'en'
  }
  // window.localStorage can be absent (Node 26 vitest jsdom) or throw
  // (privacy modes); language detection must never take the app down.
  try {
    const stored = window.localStorage.getItem(UI_LANGUAGE_STORAGE_KEY)
    if (stored) {
      if (isUiLanguage(stored)) {
        return stored
      }
      const lower = stored.toLowerCase()
      if (lower === 'pt' || lower.startsWith('pt-')) {
        const resolved = resolveUiLanguage(stored)
        window.localStorage.setItem(UI_LANGUAGE_STORAGE_KEY, resolved)
        return resolved
      }
    }
  } catch {
    // fall through to navigator detection
  }
  return resolveUiLanguage(window.navigator.language || 'en')
}

/**
 * Default content/audience language for new audiences.
 * Follows the UI chrome language so a French UI starts with French twins.
 * Study content language stays independent after the user picks a different value.
 * Portuguese chrome is `pt-BR`; generated content stays wire code `pt`.
 */
export function defaultContentLanguageFromUi(lng: string = i18n.language): string {
  return interfaceLanguageCode(lng)
}

/**
 * The interface language as a wire code (`pt-BR` is `pt`). A questionnaire
 * chat turn carries it: every text the user reads in the chat is written in
 * it, whatever language the questionnaire is in.
 */
export function interfaceLanguageCode(lng: string = i18n.language): 'en' | 'es' | 'fr' | 'pt' {
  const ui = resolveUiLanguage(lng)
  return ui === 'pt-BR' ? 'pt' : ui
}

void i18n.use(initReactI18next).init({
  resources: {
    en: { common: enCommon, auth: enAuth, onboarding: enOnboarding, app: enApp },
    es: { common: esCommon, auth: esAuth, onboarding: esOnboarding, app: esApp },
    fr: { common: frCommon, auth: frAuth, onboarding: frOnboarding, app: frApp },
    'pt-BR': PT_BR_RESOURCES,
    /* Alias so `changeLanguage('pt')` and content-language `pt` chrome still resolve. */
    pt: PT_BR_RESOURCES,
  },
  lng: detectUiLanguage(),
  fallbackLng: {
    pt: ['pt-BR', 'en'],
    'pt-PT': ['pt-BR', 'en'],
    default: ['en'],
  },
  defaultNS: 'common',
  ns: ['common', 'auth', 'onboarding', 'app'],
  interpolation: { escapeValue: false },
  returnNull: false,
})

function syncDocumentLang(lng: string) {
  if (typeof document === 'undefined') {
    return
  }
  document.documentElement.lang = resolveUiLanguage(lng)
}

syncDocumentLang(i18n.language)
i18n.on('languageChanged', syncDocumentLang)

export async function setUiLanguage(code: UiLanguage): Promise<void> {
  const resolved = resolveUiLanguage(code)
  await i18n.changeLanguage(resolved)
  if (typeof window !== 'undefined') {
    window.localStorage.setItem(UI_LANGUAGE_STORAGE_KEY, resolved)
  }
}

export default i18n
